import React, { useEffect, useMemo, useState } from 'react'
import { debounce } from 'lodash'
import { Checkbox, Dropdown, Input, Spin, Pagination } from 'antd'
import { ItemType } from 'antd/lib/menu/hooks/useItems'
import { SearchOutlined, AppstoreOutlined, BarsOutlined } from '@ant-design/icons'
import Down from 'helper/Icons/DownIcon'
import {
  useGetLeagueUpdates,
  useGetUpdateAuthors,
} from 'api/league/leagueUpdates/leagueUpdates'
import { useGetLeaguePlayers } from 'api/league/LeagueManage/leagueManage'
import { leagueUpdatesStore } from 'state/league/leagueUpdates'
import { leagueEventStore } from 'state/league/leagueEvent'
import { LeagueUpdateType } from 'types/league/leagueUpdates'
import UpdateCard from './UpdateCard'
import AddUpdateModal from '../Modals/Updates/AddUpdateModal'
import '../../styles/updatestab.scss'

const UPDATE_TYPE_OPTIONS: { label: string; value: LeagueUpdateType }[] = [
  { label: 'Live Update', value: 'liveUpdate' },
  { label: 'Announcement', value: 'announcement' },
  { label: 'Reminder', value: 'reminder' },
  { label: 'Result', value: 'result' },
  { label: 'Other', value: 'other' },
]

const Updates: React.FC = () => {
  const { leagueData } = leagueEventStore(store => ({ leagueData: store.leagueData }))
  const leagueId = leagueData?._id || ''

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<LeagueUpdateType | undefined>(undefined)
  const [authorFilter, setAuthorFilter] = useState<string | undefined>(undefined)
  const [kidFilter, setKidFilter] = useState<string | undefined>(undefined)
  const [pendingType, setPendingType] = useState<LeagueUpdateType | undefined>(undefined)
  const [pendingAuthor, setPendingAuthor] = useState<string | undefined>(undefined)
  const [pendingKid, setPendingKid] = useState<string | undefined>(undefined)
  const [open, setOpen] = useState(0)
  const [authorSearch, setAuthorSearch] = useState('')
  const [kidSearch, setKidSearch] = useState('')
  const [page, setPage] = useState(1)
  const limit = 10

  const debouncedSetSearch = useMemo(
    () => debounce((q: string) => { setSearch(q); setPage(1) }, 500),
    [],
  )

  const { addUpdateModal, editUpdateModal, setAddUpdateModal } = leagueUpdatesStore(
    store => ({
      addUpdateModal: store.addUpdateModal,
      editUpdateModal: store.editUpdateModal,
      setAddUpdateModal: store.setAddUpdateModal,
    }),
  )

  const { data, isLoading } = useGetLeagueUpdates({
    leagueId,
    type: typeFilter,
    authorId: authorFilter,
    kidId: kidFilter,
    search: search || undefined,
    page,
    limit,
  })

  const { data: authorsData } = useGetUpdateAuthors(leagueId)
  const { data: playersData } = useGetLeaguePlayers(leagueId)

  const authors = authorsData || []
  const kids = playersData?.participants?.kids || []

  // Sync pending states with applied values when dropdown opens
  useEffect(() => {
    if (open === 1) setPendingType(typeFilter)
    if (open === 2) { setPendingAuthor(authorFilter); setAuthorSearch('') }
    if (open === 3) { setPendingKid(kidFilter); setKidSearch('') }
  }, [open])

  useEffect(() => {
    setPage(1)
  }, [leagueId, typeFilter, authorFilter, kidFilter])

  const filteredAuthors = authors.filter(a =>
    a.name.toLowerCase().includes(authorSearch.toLowerCase()),
  )
  const filteredKids = kids.filter(k =>
    `${k.firstName} ${k.lastName}`.toLowerCase().includes(kidSearch.toLowerCase()),
  )

  const selectedTypeName = typeFilter
    ? UPDATE_TYPE_OPTIONS.find(o => o.value === typeFilter)?.label
    : undefined
  const selectedAuthorName = authorFilter
    ? authors.find(a => a._id === authorFilter)?.name
    : undefined
  const selectedKidName = kidFilter
    ? (() => { const k = kids.find(c => c._id === kidFilter); return k ? `${k.firstName} ${k.lastName}`.trim() : undefined })()
    : undefined

  // ── Type dropdown ────────────────────────────────────────────────────────
  const typeItems: ItemType[] = [
    {
      key: 'type-content',
      label: (
        <>
          <div className="filter-body">
            <Checkbox.Group
              className="upd-checkbox-group"
              value={pendingType ? [pendingType] : []}
              onChange={vals =>
                setPendingType(
                  vals[vals.length - 1] as LeagueUpdateType | undefined,
                )
              }
            >
              {UPDATE_TYPE_OPTIONS.map(({ label, value }) => (
                <Checkbox key={value} value={value}>{label}</Checkbox>
              ))}
            </Checkbox.Group>
          </div>
          <div className="filter-footer">
            <button
              className="clr-btn"
              onClick={() => { setPendingType(undefined); setTypeFilter(undefined); setOpen(0) }}
            >
              Clear
            </button>
            <button
              className="aply-btn"
              onClick={() => { setTypeFilter(pendingType); setOpen(0) }}
            >
              Apply
            </button>
          </div>
        </>
      ),
    },
  ]

  // ── Author dropdown ──────────────────────────────────────────────────────
  const authorItems: ItemType[] = [
    {
      key: 'author-content',
      label: (
        <>
          <div className="filter-body">
            <Input
              placeholder="Search..."
              value={authorSearch}
              onChange={e => setAuthorSearch(e.target.value)}
              style={{ marginBottom: 10 }}
            />
            {filteredAuthors.length === 0 ? (
              <p className="upd-filter-empty">No authors yet</p>
            ) : (
              <Checkbox.Group
                className="upd-checkbox-group"
                value={pendingAuthor ? [pendingAuthor] : []}
                onChange={vals =>
                  setPendingAuthor(
                    vals[vals.length - 1] as string | undefined,
                  )
                }
              >
                {filteredAuthors.map(author => (
                  <Checkbox key={author._id} value={author._id}>{author.name}</Checkbox>
                ))}
              </Checkbox.Group>
            )}
          </div>
          <div className="filter-footer">
            <button
              className="clr-btn"
              onClick={() => { setPendingAuthor(undefined); setAuthorFilter(undefined); setOpen(0) }}
            >
              Clear
            </button>
            <button
              className="aply-btn"
              onClick={() => { setAuthorFilter(pendingAuthor); setOpen(0) }}
            >
              Apply
            </button>
          </div>
        </>
      ),
    },
  ]

  // ── Kids dropdown ────────────────────────────────────────────────────────
  const kidItems: ItemType[] = [
    {
      key: 'kid-content',
      label: (
        <>
          <div className="filter-body">
            <Input
              placeholder="Search..."
              value={kidSearch}
              onChange={e => setKidSearch(e.target.value)}
              style={{ marginBottom: 10 }}
            />
            {filteredKids.length === 0 ? (
              <p className="upd-filter-empty">No Kids found</p>
            ) : (
              <Checkbox.Group
                className="upd-checkbox-group"
                value={pendingKid ? [pendingKid] : []}
                onChange={vals =>
                  setPendingKid(vals[vals.length - 1] as string | undefined)
                }
              >
                {filteredKids.map(kid => {
                  const name = `${kid.firstName} ${kid.lastName}`.trim()
                  return (
                    <Checkbox key={kid._id} value={kid._id}>{name}</Checkbox>
                  )
                })}
              </Checkbox.Group>
            )}
          </div>
          <div className="filter-footer">
            <button
              className="clr-btn"
              onClick={() => { setPendingKid(undefined); setKidFilter(undefined); setOpen(0) }}
            >
              Clear
            </button>
            <button
              className="aply-btn"
              onClick={() => { setKidFilter(pendingKid); setOpen(0) }}
            >
              Apply
            </button>
          </div>
        </>
      ),
    },
  ]

  const pinnedUpdates = data?.pinned || []
  const feedUpdates = data?.updates?.records || []
  const updatesData = data?.updates

  const isEmpty = !isLoading && pinnedUpdates.length === 0 && feedUpdates.length === 0

  return (
    <>
      <div className="py-5px bg-dark-gray"></div>
      <div className="updates-main-container">
        {/* Header row */}
        <div className="updates-header">
          <div className="updates-search-wrapper">
            <SearchOutlined className="search-icon" />
            <Input
              className="search-input-group"
              placeholder="Search updates..."
              value={searchInput}
              onChange={e => {
                setSearchInput(e.target.value)
                debouncedSetSearch(e.target.value)
              }}
              style={{ paddingLeft: 40 }}
            />
          </div>
          <div className="updates-btn-group">
            <button className="btn-add-update" onClick={() => setAddUpdateModal(true)}>
              Add update
            </button>
          </div>
        </div>

        {/* Filter bar */}
        <div className="updates-filter-bar">
          <button className="filter-icon-btn">
            <BarsOutlined style={{ fontSize: 18 }} />
          </button>
          <button className="filter-icon-btn">
            <AppstoreOutlined style={{ fontSize: 18 }} />
          </button>
          <h2 className="w-500 text-14 mr-10px">Filter by:</h2>

          <Dropdown
            menu={{ items: typeItems }}
            placement="bottomLeft"
            arrow={false}
            open={open === 1}
            onOpenChange={isOpen => setOpen(isOpen ? 1 : 0)}
            overlayClassName="custom-dropdown-overlay"
            overlayStyle={{ width: '220px' }}
          >
            <button
              className={`${open === 1 || typeFilter ? 'filter-btn-click' : 'filter-btn'} w-500`}
              onClick={() => setOpen(open === 1 ? 0 : 1)}
            >
              {selectedTypeName ?? 'Type'} <Down />
            </button>
          </Dropdown>

          <Dropdown
            menu={{ items: authorItems }}
            placement="bottomLeft"
            arrow={false}
            open={open === 2}
            onOpenChange={isOpen => setOpen(isOpen ? 2 : 0)}
            overlayClassName="custom-dropdown-overlay"
            overlayStyle={{ width: '260px' }}
          >
            <button
              className={`${open === 2 || authorFilter ? 'filter-btn-click' : 'filter-btn'} w-500`}
              onClick={() => setOpen(open === 2 ? 0 : 2)}
            >
              {selectedAuthorName ?? 'Author'} <Down />
            </button>
          </Dropdown>

          <Dropdown
            menu={{ items: kidItems }}
            placement="bottomLeft"
            arrow={false}
            open={open === 3}
            onOpenChange={isOpen => setOpen(isOpen ? 3 : 0)}
            overlayClassName="custom-dropdown-overlay"
            overlayStyle={{ width: '260px' }}
          >
            <button
              className={`${open === 3 || kidFilter ? 'filter-btn-click' : 'filter-btn'} w-500`}
              onClick={() => setOpen(open === 3 ? 0 : 3)}
            >
              {selectedKidName ?? 'Kids'} <Down />
            </button>
          </Dropdown>
        </div>

        {/* Feed */}
        {isLoading ? (
          <div className="updates-empty">
            <Spin />
          </div>
        ) : isEmpty ? (
          <div className="updates-empty">
            <p>No updates yet. Click &quot;Add update&quot; to post the first one.</p>
          </div>
        ) : (
          <>
            <div className="updates-feed">
              {pinnedUpdates.map(update => (
                <UpdateCard key={update._id} update={update} />
              ))}
              {feedUpdates.map(update => (
                <UpdateCard key={update._id} update={update} />
              ))}
            </div>

            {updatesData && updatesData.totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
                <Pagination
                  current={page}
                  pageSize={limit}
                  total={updatesData.count}
                  onChange={p => setPage(p)}
                  showSizeChanger={false}
                />
              </div>
            )}
          </>
        )}
      </div>

      {(addUpdateModal || editUpdateModal) && <AddUpdateModal />}
    </>
  )
}

export default Updates
